/**
 * Quick-log controls for measurable habits (migration 0022).
 *
 * Two shapes, chosen by the habit's measurement_kind:
 *  - count / duration / quantity: a row of one-tap "+N" chips built from
 *    quick_increments (falling back to a single batch_size chip) plus a
 *    keyboard-icon chip that opens a small numeric field. Every path posts
 *    POST /api/habits/{id}/log/increment {value} — one transport for chips
 *    and typed amounts alike, so the server-side clamping story never forks.
 *  - opaque: a compact numeric field that sets the day's exact value via
 *    POST /api/habits/{id}/log {value} — the only way to log OPAQUE. The
 *    value is stored verbatim; nothing here interprets it.
 *
 * Self-posting (house pattern: CheckInWindowsEditor) so the Habits and
 * Check-In pages share the exact transports instead of re-implementing them.
 */
import React, { useState } from 'react';
import { Box, Chip, IconButton, TextField, Tooltip } from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export type HabitMeasurementKind =
  | 'binary'
  | 'count'
  | 'duration'
  | 'quantity'
  | 'opaque';

export interface QuickLogHabit {
  id: number;
  title: string;
  measurement_kind?: HabitMeasurementKind | string | null;
  unit_label?: string | null;
  batch_size?: number | null;
  quick_increments?: number[] | null;
}

/** True when the kind interprets logged numbers as honest units — the kinds
 * that may receive +value increments. OPAQUE and BINARY are excluded. */
export const isMeasurableKind = (kind?: string | null): boolean =>
  kind === 'count' || kind === 'duration' || kind === 'quantity';

/** Render a measured value without float dust: at most 3 decimals (the
 * server rounds stored sums the same way), trailing zeros trimmed, so
 * counts stay whole numbers and honest decimals stay visible. */
export const formatMeasureValue = (value: number): string =>
  String(Math.round(value * 1000) / 1000);

/** The one-tap chip values to offer: quick_increments verbatim (≤8 by API
 * contract), else the batch size as a single chip, else none. */
export const quickIncrementValues = (habit: QuickLogHabit): number[] => {
  if (habit.quick_increments && habit.quick_increments.length > 0) {
    return habit.quick_increments.slice(0, 8);
  }
  if (habit.batch_size && habit.batch_size > 0) return [habit.batch_size];
  return [];
};

/** YYYY-MM-DD for the user's current local calendar day (same helper as the
 * pages — quick actions must never let the server guess "today" from UTC). */
const todayLocalYmd = (): string => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(
    t.getDate(),
  ).padStart(2, '0')}`;
};

interface Props {
  habit: QuickLogHabit;
  size?: 'small' | 'medium';
  onLogged: () => void | Promise<void>;
  onError: (message: string) => void;
}

const HabitQuickLogChips: React.FC<Props> = ({
  habit,
  size = 'small',
  onLogged,
  onError,
}) => {
  const [busy, setBusy] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [opaqueValue, setOpaqueValue] = useState('');

  const isOpaque = habit.measurement_kind === 'opaque';
  const chipValues = isMeasurableKind(habit.measurement_kind)
    ? quickIncrementValues(habit)
    : [];

  const postIncrement = async (value: number) => {
    try {
      setBusy(true);
      await axios.post(`${API_BASE_URL}/api/habits/${habit.id}/log/increment`, {
        value,
        date_local: todayLocalYmd(),
      });
      setCustomValue('');
      setCustomOpen(false);
      await onLogged();
    } catch (err: any) {
      onError(getApiErrorMessage(err, 'Failed to log habit'));
    } finally {
      setBusy(false);
    }
  };

  const submitCustom = () => {
    const parsed = Number(customValue);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      onError('Enter a positive amount');
      return;
    }
    postIncrement(parsed);
  };

  const submitOpaque = async () => {
    const parsed = Number(opaqueValue);
    if (opaqueValue.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
      onError('Enter a value of 0 or more');
      return;
    }
    try {
      setBusy(true);
      // The canonical OPAQUE entry point: sets the day's value verbatim.
      // date as the local YYYY-MM-DD keeps the user's calendar day intact.
      await axios.post(`${API_BASE_URL}/api/habits/${habit.id}/log`, {
        date: todayLocalYmd(),
        value: parsed,
      });
      setOpaqueValue('');
      await onLogged();
    } catch (err: any) {
      onError(getApiErrorMessage(err, 'Failed to log habit'));
    } finally {
      setBusy(false);
    }
  };

  if (isOpaque) {
    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <TextField
          size="small"
          type="number"
          placeholder="Value"
          value={opaqueValue}
          disabled={busy}
          onChange={(e) => setOpaqueValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitOpaque();
          }}
          inputProps={{ step: 'any', min: 0, 'aria-label': `Log value for ${habit.title}` }}
          sx={{ width: 110 }}
        />
        <Tooltip title="Save today's value">
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={busy}
              onClick={submitOpaque}
              aria-label={`Save value for ${habit.title}`}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  if (chipValues.length === 0) return null;

  return (
    <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
      {chipValues.map((value) => {
        const label = `+${formatMeasureValue(value)}`;
        return (
          <Chip
            key={value}
            size={size}
            label={label}
            color="primary"
            variant="outlined"
            clickable
            disabled={busy}
            onClick={() => postIncrement(value)}
            aria-label={`Log ${label} for ${habit.title}`}
          />
        );
      })}
      <Chip
        size={size}
        icon={<KeyboardIcon />}
        label=""
        variant="outlined"
        clickable
        disabled={busy}
        onClick={() => setCustomOpen((open) => !open)}
        aria-label={`Log a custom amount for ${habit.title}`}
        sx={{ '& .MuiChip-label': { pl: 0.5 } }}
      />
      {customOpen && (
        <Box display="flex" alignItems="center" gap={0.5}>
          <TextField
            size="small"
            type="number"
            placeholder="Amount"
            value={customValue}
            disabled={busy}
            autoFocus
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitCustom();
            }}
            inputProps={{
              step: 'any',
              min: 0,
              'aria-label': `Custom amount for ${habit.title}`,
            }}
            sx={{ width: 100 }}
          />
          <IconButton
            size="small"
            color="primary"
            disabled={busy}
            onClick={submitCustom}
            aria-label={`Save custom amount for ${habit.title}`}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default HabitQuickLogChips;
